import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Avatar,
  Typography,
  Stack,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Category as CategoryIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function AddCategoryDialog({
  open,
  onClose,
  onSave,
  categoryName,
  onCategoryNameChange,
  isLoading,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && categoryName.trim() !== "") {
      onSave();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              border: "2px solid",
              borderColor: alpha(theme.palette.success.main, 0.2),
            }}
          >
            <CategoryIcon sx={{ color: "success.main" }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {t("precautions.addCategoryDialogTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("precautions.addCategoryDialogSubtitle")}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 3, pb: 2 }}>
        <TextField
          autoFocus
          label={t("precautions.categoryNameLabel")}
          fullWidth
          value={categoryName}
          onChange={(e) => onCategoryNameChange(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: theme.shadows[2],
              },
              "&.Mui-focused": {
                boxShadow: theme.shadows[4],
              },
            },
          }}
          helperText={t("precautions.categoryNameHelper")}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("precautions.cancelButton")}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="success"
          disabled={isLoading || categoryName.trim() === ""}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${alpha(theme.palette.success.main, 0.8)} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.9)} 0%, ${alpha(theme.palette.success.main, 0.7)} 100%)`,
              boxShadow: theme.shadows[6],
            },
            "&.Mui-disabled": {
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.6)} 0%, ${alpha(theme.palette.grey[300], 0.4)} 100%)`,
              color: alpha(theme.palette.text.disabled, 0.7),
            },
          }}
        >
          {t("precautions.addButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddCategoryDialog;
